import React, {Suspense, useEffect, useState} from 'react';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import EVBatteryPerformanceStatus from '@di-shared/features/home/kr/ev-battery/monitor/EvBatteryPerformance';
import EVBatteryChargingHabit from '@di-shared/features/home/kr/ev-battery/monitor/EVBatteryChargingHabit';
import {ErrorBoundary} from 'react-error-boundary';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {GetEvBatteryManufacturerResponse} from '@di-shared/api/di/__generated';
import {BatteryManufacturerName} from '@di-shared/features/home/kr/ev-battery/monitor/const';
import {TranslationKey} from '@di-shared/i18next';
import ChargingEventNotification from '@di-shared/features/home/kr/ev-battery/monitor/ChargingEventNotification';
import {emitEventLog} from '@di-shared/features/analytics';

interface EvBatteryMonitorProps {
  vin: string;
  puid: string;
}

function EvBatteryMonitor(props: EvBatteryMonitorProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<Loading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, puid}: EvBatteryMonitorProps) {
  const {t} = useInferableTranslation();
  const {
    data: {data},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.evBatteryController.getEvBatteryStatus), vin, puid],
    queryFn: () => diApi.evBatteryController.getEvBatteryStatus({vin, puid}),
  });
  const {soc, soh} = data;

  useEffect(() => {
    emitEventLog({
      event: 'ev_battery',
      data_action: 'load',
    });
  }, []);

  /**
   * [2025-07-24:Jeyoung Lee]
   * @link https://jira.hmg-corp.io/browse/DRIVINGINS-1106
   * SoC가 0 이거나, SoH가 0일 경우 컴포넌트 미노출
   */

  if (soc === 0 || soh === 0) {
    return <></>;
  }

  return (
    <section className="section section_ev_battery">
      <div className="section_container">
        <div className="section_container_header">
          <h3 className="title_xxsmall">{t('T30444')}</h3>
        </div>
        <ErrorBoundary fallback={<></>}>
          <BatteryManufacturer
            vin={vin}
            puid={puid}
          />
        </ErrorBoundary>
        <BatteryManagementGuideMessage />
        <ChargingEventNotification
          vin={vin}
          puid={puid}
        />
        <div className="battery_status_area">
          <EVBatteryPerformanceStatus
            vin={vin}
            puid={puid}
          />
          <EVBatteryChargingHabit
            vin={vin}
            puid={puid}
          />
        </div>
      </div>
    </section>
  );
}

function Loading() {
  return (
    <section className="section section_ev_battery">
      <div
        className="section_container skeleton"
        style={{height: 330}}
      />
    </section>
  );
}

export default EvBatteryMonitor;

interface BatteryManufacturerProps {
  vin: string;
  puid: string;
}

function BatteryManufacturer({vin, puid}: BatteryManufacturerProps) {
  const {t} = useInferableTranslation();
  const {
    data: {data},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.evBatteryController.getEvBatteryManufacturer), vin, puid],
    queryFn: () => diApi.evBatteryController.getEvBatteryManufacturer({vin, puid}),
  });
  if (!data?.batteryManufacturer || data.batteryManufacturer === GetEvBatteryManufacturerResponse.batteryManufacturer.UNKNOWN) {
    return <></>;
  }
  return (
    <p className="section_container_desc">
      {t('T30445')}: {BatteryManufacturerName[data.batteryManufacturer]}
    </p>
  );
}

function BatteryManagementGuideMessage() {
  const {t} = useInferableTranslation();
  const messageKey: TranslationKey[] = ['T30446', 'T30447', 'T30448', 'T30449', 'T30450'];
  const [message] = useState(t(messageKey[Math.floor(Math.random() * messageKey.length)]));
  return <p className="battery_guide_text">{message}</p>;
}
